import React, { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FileText, Plus, Trash2, ArrowUp, ArrowDown, Pencil, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import { REASONS } from "@/components/booking/MedicalAlerts";
import { FormPreview } from "@/components/booking/ClientPreview";

const uid = () => Math.random().toString(36).slice(2, 10);
const ELEMENTS = [
  ["heading", "Heading"], ["text", "Text block"], ["checkbox", "Checkbox"], ["choose", "Choose from list"],
  ["short", "Short answer"], ["long", "Long answer"], ["date", "Date"], ["signature", "Signature"], ["client_details", "Client details"],
];

export default function Forms() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template object being edited
  const [preview, setPreview] = useState(null);
  const load = () => api.get("/forms/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const newTemplate = () => setEditing({ name: "", description: "", elements: [] });
  const openEdit = (t) => setEditing({ ...t, elements: (t.elements || []).map((e) => ({ ...e })) });

  const addEl = (type) => setEditing((s) => ({ ...s, elements: [...s.elements, { id: uid(), type, label: "", required: false, options: [] }] }));
  const updEl = (i, k, v) => setEditing((s) => ({ ...s, elements: s.elements.map((e, j) => j === i ? { ...e, [k]: v } : e) }));
  const rmEl = (i) => setEditing((s) => ({ ...s, elements: s.elements.filter((_, j) => j !== i) }));
  const move = (i, d) => setEditing((s) => { const a = [...s.elements]; const j = i + d; if (j < 0 || j >= a.length) return s; [a[i], a[j]] = [a[j], a[i]]; return { ...s, elements: a }; });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Give the template a name");
    const payload = { name: editing.name, description: editing.description || "", elements: editing.elements };
    try {
      if (editing.id) await api.put(`/forms/templates/${editing.id}`, payload);
      else await api.post("/forms/templates", payload);
      toast.success("Template saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  const remove = async (t) => { if (!window.confirm(`Delete the "${t.name}" template? This can't be undone.`)) return; await api.delete(`/forms/templates/${t.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="forms-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight flex items-center gap-2"><FileText className="h-7 w-7 text-primary" />Consultation forms</h1>
          <p className="text-muted-foreground mt-1">Build templates and send them to clients to complete online.</p>
        </div>
        <Button className="rounded-lg" onClick={newTemplate} data-testid="new-template-button"><Plus className="h-4 w-4 mr-1.5" />New template</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <Card key={t.id} className="rounded-2xl border p-5 shadow-sm flex flex-col" data-testid={`template-card-${t.id}`}>
            <div className="font-heading font-semibold">{t.name}</div>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{t.description || `${(t.elements || []).length} fields`}</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" className="rounded-lg flex-1" onClick={() => openEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
              <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setPreview(t)} data-testid={`template-preview-${t.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
              <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
        {templates.length === 0 && <p className="text-muted-foreground">No templates yet — create your first.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-editor">
          <DialogHeader><DialogTitle className="font-heading">{editing?.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Input placeholder="Template name" value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} className="h-11 rounded-lg" data-testid="template-name" />
              <Input placeholder="Short description (optional)" value={editing.description} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} className="h-10 rounded-lg" data-testid="template-desc" />

              <div className="space-y-3">
                {editing.elements.map((el, i) => (
                  <div key={el.id} className="rounded-xl border border-border p-3 space-y-2" data-testid={`el-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground">{ELEMENTS.find((x) => x[0] === el.type)?.[1] || el.type}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground"><ArrowUp className="h-4 w-4" /></button>
                        <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground"><ArrowDown className="h-4 w-4" /></button>
                        <button onClick={() => rmEl(i)} className="text-muted-foreground hover:text-destructive" data-testid={`el-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    {el.type !== "client_details" && (
                      <Input placeholder={el.type === "text" ? "Paragraph text" : "Field label / question"} value={el.label} onChange={(e) => updEl(i, "label", e.target.value)} className="h-9 rounded-lg" data-testid={`el-label-${i}`} />
                    )}
                    {el.type === "choose" && (
                      <Input placeholder="Options, comma separated" value={(el.options || []).join(", ")} onChange={(e) => updEl(i, "options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="h-9 rounded-lg" data-testid={`el-options-${i}`} />
                    )}
                    {!["heading", "text", "client_details"].includes(el.type) && (
                      <label className="flex items-center gap-2 text-sm"><Switch checked={!!el.required} onCheckedChange={(v) => updEl(i, "required", v)} data-testid={`el-required-${i}`} />Required</label>
                    )}
                    {["short", "long", "choose", "checkbox"].includes(el.type) && (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        <Select value={el.alert_reason || "none"} onValueChange={(v) => updEl(i, "alert_reason", v === "none" ? "" : v)}>
                          <SelectTrigger className="h-9 rounded-lg text-xs" data-testid={`el-alert-${i}`}><SelectValue placeholder="Raise a medical alert when answered…" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">No medical alert</SelectItem>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>Alert: {l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Add element</div>
                <div className="flex flex-wrap gap-2">
                  {ELEMENTS.map(([type, label]) => (
                    <Button key={type} variant="outline" size="sm" className="rounded-lg" onClick={() => addEl(type)} data-testid={`add-el-${type}`}><Plus className="h-3.5 w-3.5 mr-1" />{label}</Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" className="rounded-lg" onClick={() => setPreview(editing)} data-testid="template-preview"><Eye className="h-4 w-4 mr-1.5" />Preview</Button>
            <Button variant="outline" className="rounded-lg" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} data-testid="template-save">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FormPreview template={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
